import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import ProductCard from "@/components/ProductCard";
import Newsletter from "@/components/Newsletter";
import { ArrowRight } from "lucide-react";
import {
  Daisy,
  FivePetal,
  Leaf,
  Scribble,
  SquiggleArrow,
  WaveTop,
  WaveBottom,
  PeonyBloom,
  RoseBud,
  BotanicalBranch,
  Sparkle,
} from "@/components/Decor";

/* ---------- Assets (user-owned photos only) ---------- */
const HERO_MAIN = "https://customer-assets.emergentagent.com/job_la-fleur-studio/artifacts/20amxwqp_IMG_6240.webp";
const HERO_SIDE = "https://customer-assets.emergentagent.com/job_la-fleur-studio/artifacts/gsca3f32_IMG_9802.jpg";
const BALLOON   = "https://customer-assets.emergentagent.com/job_la-fleur-studio/artifacts/gg8q9hra_IMG_5740.webp";
const BALLOON_2 = "https://customer-assets.emergentagent.com/job_la-fleur-studio/artifacts/dhaoq14v_IMG_7988%202.webp";
const BALLOON_3 = "https://customer-assets.emergentagent.com/job_la-fleur-studio/artifacts/1z4wgzvb_IMG_5487.webp";

/* Category tile images — all user-owned */
const CAT_ROSES    = "https://customer-assets-7cd3h4nn.emergentagent.net/job_la-fleur-studio/artifacts/6jnmsnin_IMG_1066.webp"; // pure pink roses at LA FLEUR SHOP wall (needs rotation)
const CAT_MIX      = "https://customer-assets-7cd3h4nn.emergentagent.net/job_la-fleur-studio/artifacts/0zneitbf_IMG_1423.webp"; // pastel mix bouquet — "Congrats Grad"
const CAT_VASE     = "https://customer-assets.emergentagent.com/job_la-fleur-studio/artifacts/b6qyl52j_Vase%20A5.jpeg";
const CAT_BALLOONS = "https://customer-assets.emergentagent.com/job_la-fleur-studio/artifacts/gg8q9hra_IMG_5740.webp";

const CATEGORY_CARDS = [
  { to: "/shop?category=bouquets",   label: "Rose Bouquets", img: CAT_ROSES,    bg: "var(--lf-pink-pale)", rotate: 90 },
  { to: "/shop?category=bouquets",   label: "Mix Florals",   img: CAT_MIX,      bg: "var(--lf-mint)" },
  { to: "/shop?category=bouquets",   label: "Vase Designs",  img: CAT_VASE,     bg: "var(--lf-lavender)" },
  { to: "/shop?category=balloons",   label: "Balloon Bar",   img: CAT_BALLOONS, bg: "var(--lf-cream-2)" },
];

const Home = () => {
  const [bouquets, setBouquets] = useState([]);
  useEffect(() => {
    api.get("/products?category=bouquets").then(({ data }) => setBouquets(data));
  }, []);

  return (
    <div data-testid="home-page">
      {/* =================== TOP MARQUEE =================== */}
      <div className="lf-marquee" data-testid="home-top-marquee">
        <Marquee gradient={false} speed={32} pauseOnHover>
          {Array.from({ length: 4 }).map((_, i) => (
            <React.Fragment key={i}>
              <span className="lf-marquee-text">Same-day delivery</span>
              <span className="lf-marquee-dot" />
              <span className="lf-marquee-text">The Flower Bar</span>
              <span className="lf-marquee-dot" />
              <span className="lf-marquee-text">Bridal · Memorial · Custom</span>
              <span className="lf-marquee-dot" />
              <span className="lf-marquee-text">Pickup · Delivery · Shipping</span>
              <span className="lf-marquee-dot" />
            </React.Fragment>
          ))}
        </Marquee>
      </div>

      {/* =================== HERO =================== */}
      <section
        className="relative overflow-hidden"
        style={{ background: "var(--lf-pink-pale)" }}
        data-testid="home-hero"
      >
        <div className="lf-container relative z-10 pt-20 md:pt-24 pb-20 md:pb-28 text-center">
          <h1
            className="font-cursive lf-gold-shimmer"
            style={{
              fontSize: "clamp(4rem, 10vw, 9rem)",
              margin: "0 auto",
              whiteSpace: "nowrap",
            }}
            data-testid="hero-brand"
          >
            La Fleur
          </h1>
          <p
            className="font-cursive lf-gold-shimmer"
            style={{
              fontSize: "clamp(2.5rem, 6.5vw, 5.5rem)",
              margin: "-0.05em auto 0",
              whiteSpace: "nowrap",
            }}
            data-testid="hero-brand-sub"
          >
            Flower Shop
          </p>
          <p
            className="font-script mt-6"
            style={{
              fontSize: "clamp(1.6rem, 3.4vw, 2.5rem)",
              color: "var(--lf-pink-deep)",
            }}
            data-testid="hero-tagline"
          >
            luxury in every bloom
          </p>
          <div className="mt-5">
            <span className="lf-eyebrow" data-testid="hero-eyebrow">Fresh blooms · Graham, NC</span>
          </div>
          <Scribble className="lf-scribble" />

          {/* Two tilted polaroid photos */}
          <div className="mt-16 md:mt-20 flex flex-wrap justify-center items-center gap-6 md:gap-10 relative">
            <motion.div
              initial={{ opacity: 0, y: 24, rotate: -10 }}
              animate={{ opacity: 1, y: 0, rotate: -6 }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="lf-polaroid"
              style={{
                width: "min(340px, 78vw)",
                aspectRatio: "3 / 4",
                zIndex: 2,
              }}
              data-testid="hero-photo-main"
            >
              <img src={HERO_MAIN} alt="Pink lily and rose bouquet at La Fleur Floreria" />
              <span className="lf-polaroid-caption">fresh today</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24, rotate: 10 }}
              animate={{ opacity: 1, y: 0, rotate: 5 }}
              transition={{ duration: 0.9, delay: 0.35 }}
              className="lf-polaroid"
              style={{
                width: "min(340px, 78vw)",
                aspectRatio: "3 / 4",
                zIndex: 1,
              }}
              data-testid="hero-photo-side"
            >
              <img src={HERO_SIDE} alt="Burgundy and pink rose crown bouquet at La Fleur Flower Shop" />
              <span className="lf-polaroid-caption">signature bouquet</span>
            </motion.div>
          </div>

          <h2
            className="font-serif mt-14 leading-tight"
            style={{
              fontSize: "clamp(1.35rem, 2.6vw, 2rem)",
              fontWeight: 300,
              color: "var(--lf-ink)",
              letterSpacing: "0.01em",
            }}
            data-testid="hero-subheadline"
          >
            Beautiful flowers <em style={{ color: "var(--lf-gold-deep)" }}>for every occasion.</em>
          </h2>

          <p
            className="mt-5 max-w-xl mx-auto text-sm md:text-base leading-relaxed"
            style={{ color: "var(--lf-ink-soft)" }}
          >
            Rose bouquets, mixed florals, a balloon bar, sweets, merch, and bespoke
            designs — all hand-tied in our boutique on South Main Street.
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-3" data-testid="hero-cta-row">
            <Link to="/shop" className="lf-btn lf-btn-gold" data-testid="hero-shop-button">
              Shop now <ArrowRight size={15} />
            </Link>
            <Link to="/services/flower-bar" className="lf-btn lf-btn-pink" data-testid="hero-flower-bar-button">
              The Flower Bar
            </Link>
          </div>
        </div>
      </section>

      {/* Wavy transition into pink pale section */}
      <div style={{ background: "var(--lf-cream)", lineHeight: 0 }}>
        <WaveBottom fill="var(--lf-pink-pale)" />
      </div>

      {/* =================== SHOP BY COLLECTION =================== */}
      <section
        className="relative py-20 md:py-28"
        style={{ background: "var(--lf-pink-pale)" }}
        data-testid="home-categories"
      >
        <Daisy color="var(--lf-pink)" size={80}
          className="lf-decor lf-float-slow hidden md:block"
          style={{ top: 40, left: 60, "--r": "18deg" }} />
        <SquiggleArrow className="lf-decor hidden lg:block" size={140}
          style={{ top: 90, right: "12%", transform: "rotate(-20deg)" }} />

        <div className="lf-container relative z-10">
          <div className="text-center max-w-xl mx-auto mb-14 md:mb-16">
            <span className="lf-eyebrow" style={{ color: "var(--lf-pink-deep)" }}>Shop by mood</span>
            <h2
              className="font-serif mt-3 leading-none"
              style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 300, color: "var(--lf-ink)" }}
            >
              Find <em style={{ color: "var(--lf-gold-deep)" }}>your moment.</em>
            </h2>
            <p className="font-script text-2xl md:text-3xl mt-2" style={{ color: "var(--lf-pink-deep)" }}>
              shop by collection
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {CATEGORY_CARDS.map((c, i) => (
              <motion.div
                key={c.label}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
              >
                <Link
                  to={c.to}
                  data-testid={`category-tile-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group relative block overflow-hidden"
                  style={{
                    background: c.bg,
                    borderRadius: 28,
                    aspectRatio: "4 / 5",
                  }}
                >
                  <img
                    src={c.img}
                    alt={c.label}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    style={c.rotate ? {
                      transform: `rotate(${c.rotate}deg) scale(1.6)`,
                      transformOrigin: "center center",
                    } : undefined}
                  />
                  <span className="lf-cat-pill">{c.label}</span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Wavy transition back to cream */}
      <div style={{ background: "var(--lf-pink-pale)", lineHeight: 0 }}>
        <WaveBottom fill="var(--lf-cream)" />
      </div>

      {/* =================== FEATURED BOUQUETS =================== */}
      <section
        className="relative py-20 md:py-28 overflow-hidden"
        style={{ background: "var(--lf-cream)" }}
        data-testid="home-bouquets"
      >
        <FivePetal color="var(--lf-pink)" size={90}
          className="lf-decor lf-float-medium hidden md:block"
          style={{ top: 60, right: 30, "--r": "-14deg" }} />
        <Leaf color="var(--lf-mint)" size={110}
          className="lf-decor lf-float-slow hidden md:block"
          style={{ bottom: 80, left: 30, "--r": "22deg" }} />

        <div className="lf-container relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="lf-eyebrow">Handpicked for you</span>
            <h2
              className="font-serif mt-3 leading-none"
              style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 300, color: "var(--lf-ink)" }}
            >
              Our <em style={{ color: "var(--lf-gold-deep)" }}>favorites.</em>
            </h2>
            <Scribble className="lf-scribble" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {bouquets.slice(0, 6).map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              >
                <ProductCard product={p} tone={["pink", "mint", "lav", "cream"][i % 4]} />
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-14" data-testid="bouquets-view-all-wrap">
            <Link to="/shop?category=bouquets" className="lf-btn lf-btn-outline" data-testid="bouquets-view-all">
              View all bouquets <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* =================== SALE / TAGLINE MARQUEE =================== */}
      <div
        className="py-3"
        style={{ background: "var(--lf-mint)", borderTop: "1px solid var(--lf-mint-deep)", borderBottom: "1px solid var(--lf-mint-deep)" }}
        data-testid="home-mid-marquee"
      >
        <Marquee gradient={false} speed={28} pauseOnHover>
          {Array.from({ length: 5 }).map((_, i) => (
            <React.Fragment key={i}>
              <span className="font-script text-2xl mx-6" style={{ color: "var(--lf-ink)" }}>
                luxury in every bloom
              </span>
              <span
                className="inline-block align-middle mx-4"
                style={{ width: 8, height: 8, borderRadius: 999, background: "var(--lf-gold-deep)" }}
              />
              <span className="font-serif italic text-lg mx-6" style={{ color: "var(--lf-ink)" }}>
                fresh · local · Graham NC
              </span>
              <span
                className="inline-block align-middle mx-4"
                style={{ width: 8, height: 8, borderRadius: 999, background: "var(--lf-gold-deep)" }}
              />
            </React.Fragment>
          ))}
        </Marquee>
      </div>

      {/* =================== BALLOON BAR =================== */}
      <section
        className="relative py-20 md:py-28 overflow-hidden"
        style={{ background: "var(--lf-cream)" }}
        data-testid="home-balloon-bar"
      >
        <div className="lf-container relative z-10">
          <div
            className="relative overflow-hidden px-6 py-14 md:px-14 md:py-20"
            style={{ background: "var(--lf-mint)", borderRadius: "3rem" }}
          >
            {/* Decor inside the mint block */}
            <FivePetal color="var(--lf-pink)" size={100}
              className="lf-decor lf-float-medium"
              style={{ top: 30, right: 30, "--r": "24deg" }} />
            <Leaf color="var(--lf-mint-deep)" size={140}
              className="lf-decor lf-float-slow"
              style={{ bottom: -20, left: -10, "--r": "-30deg", opacity: 0.7 }} />
            <Daisy color="var(--lf-yellow)" size={70}
              className="lf-decor lf-float-fast hidden md:block"
              style={{ bottom: 40, right: "35%", "--r": "10deg" }} />

            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-5">
                <span className="lf-eyebrow" style={{ color: "var(--lf-pink-deep)" }}>Our best-kept secret</span>
                <h2
                  className="font-serif mt-4 leading-[0.95]"
                  style={{ fontSize: "clamp(2.25rem, 6vw, 4.75rem)", fontWeight: 300, color: "var(--lf-ink)" }}
                >
                  The
                  <br />
                  <em style={{ color: "var(--lf-gold-deep)" }}>Balloon Bar.</em>
                </h2>
                <p
                  className="mt-6 max-w-md text-base md:text-lg leading-relaxed"
                  style={{ color: "var(--lf-ink-soft)" }}
                >
                  Latex balloons at <strong>$3</strong>, foil at <strong>$4</strong> — plus
                  custom installations for birthdays, showers, brand launches, and proposals.
                  Pick a palette, we build the moment.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/shop?category=balloons" className="lf-btn lf-btn-gold" data-testid="balloon-shop-button">
                    Shop balloons <ArrowRight size={15} />
                  </Link>
                  <a href="tel:+13362690356" className="lf-btn lf-btn-outline" data-testid="balloon-call-button">
                    Call for custom
                  </a>
                </div>
              </div>

              {/* Photo collage — 3 polaroids overlapping */}
              <div className="lg:col-span-7 relative min-h-[440px] md:min-h-[520px]">
                <div
                  className="lf-polaroid absolute"
                  style={{
                    width: "min(300px, 62%)",
                    aspectRatio: "3 / 4",
                    top: "0%", left: "5%",
                    transform: "rotate(-6deg)",
                    zIndex: 3,
                  }}
                  data-testid="balloon-photo-1"
                >
                  <img src={BALLOON} alt="Mauve bow balloon with pastel yellow balloons and birthday bouquet" />
                </div>
                <div
                  className="lf-polaroid absolute"
                  style={{
                    width: "min(240px, 52%)",
                    aspectRatio: "1 / 1",
                    top: "38%", right: "0%",
                    transform: "rotate(6deg)",
                    zIndex: 2,
                  }}
                  data-testid="balloon-photo-2"
                >
                  <img src={BALLOON_2} alt="Red, black, gold and white balloon bouquets over pink counter" />
                </div>
                <div
                  className="lf-polaroid absolute"
                  style={{
                    width: "min(220px, 46%)",
                    aspectRatio: "1 / 1",
                    bottom: "0%", left: "26%",
                    transform: "rotate(-3deg)",
                    zIndex: 1,
                  }}
                  data-testid="balloon-photo-3"
                >
                  <img src={BALLOON_3} alt="Custom USMC-themed balloon bouquet at the Balloon Bar" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =================== MISSION PULL QUOTE =================== */}
      <section
        className="relative py-20 md:py-32 overflow-hidden"
        style={{ background: "var(--lf-cream)" }}
        data-testid="home-mission"
      >
        <Daisy color="var(--lf-pink-deep)" size={90}
          className="lf-decor lf-float-slow hidden md:block"
          style={{ top: 50, left: "8%", "--r": "-12deg" }} />
        <Daisy color="var(--lf-lavender)" size={80}
          className="lf-decor lf-float-medium hidden md:block"
          style={{ bottom: 80, right: "10%", "--r": "16deg" }} />
        <Leaf color="var(--lf-mint)" size={110}
          className="lf-decor lf-float-slow hidden lg:block"
          style={{ top: 120, right: "18%", "--r": "40deg" }} />

        <div className="lf-container relative z-10 text-center max-w-3xl">
          <span className="lf-eyebrow">Our mission</span>
          <blockquote
            className="font-serif mt-6 leading-[1.05]"
            style={{ fontSize: "clamp(2.25rem, 6vw, 4.5rem)", fontWeight: 300, color: "var(--lf-ink)" }}
          >
            From a single stem to a
            {" "}<em style={{ color: "var(--lf-gold-deep)" }}>200-rose</em>{" "}
            bouquet — every order is hand-finished in our Graham studio, wrapped in our
            signature paper, and delivered with{" "}
            <span className="font-script" style={{ fontSize: "1.1em", color: "var(--lf-pink-deep)" }}>care</span>.
          </blockquote>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/about" className="lf-btn lf-btn-mint" data-testid="mission-about-button">
              About La Fleur
            </Link>
            <a href="tel:+13362690356" className="lf-btn lf-btn-outline" data-testid="mission-call-button">
              Call (336) 269-0356
            </a>
          </div>
        </div>
      </section>

      {/* =================== NEWSLETTER =================== */}
      <Newsletter />
    </div>
  );
};

export default Home;
;
